import { useEffect, useState } from 'react';
import { Banner, FlexContainer, Icon, Layout, Sizes, Spinner, Text } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import { useTranslation } from 'Hooks/useTranslation';
import { PreviousYearSatStatus } from 'Containers/PreviousYears/types';
import { TAX_YEAR } from 'Config';
import { YearDataProps } from './types';
import * as Styled from './styles';

const TAX_YEAR_2019 = '2019';

const YearData = ({ data }: YearDataProps): JSX.Element => {
  const { t } = useTranslation(locizeNS.PY);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false);
    }, 5000);
  }, []);
  const sortArrPerYear = data.records
    .sort((a, b) => Number(a.taxYear) - Number(b.taxYear))
    .filter(item => item.isRecoverable);
  const missingSatStatus = data?.records?.filter(
    item =>
      item.lastStatus === PreviousYearSatStatus.NOT_AVAILABLE &&
      item.taxYear !== TAX_YEAR &&
      item.taxYear !== TAX_YEAR_2019
  );
  const taxYear2019 = data?.records?.find(
    item => item.taxYear === TAX_YEAR_2019 && item.amountTotal && Math.abs(item.amountTotal) > 0
  );
  const yearsMissingSatStatus = missingSatStatus?.map(item => item.taxYear);

  const recordsToShow = [
    ...(taxYear2019 ? [taxYear2019] : []),
    ...sortArrPerYear.filter(item => item.taxYear !== TAX_YEAR_2019),
  ];

  return (
    <FlexContainer direction="column" fullWidth gap="dimension_16">
      <FlexContainer gap="dimension_08" direction="column">
        {isLoading ? (
          <FlexContainer align="center" justify="center">
            <Spinner size="M" align="center" />
          </FlexContainer>
        ) : (
          <>
            {recordsToShow?.map(item => (
              <Layout.Wrap size="S" key={item.taxYear} hasBorder>
                <FlexContainer direction="column" gap="dimension_08">
                  <FlexContainer align="center" justify="space-between">
                    <FlexContainer align="center" gap="dimension_04">
                      <Icon
                        icon={
                          item.taxYear === TAX_YEAR_2019
                            ? 'WarningRoundedSolidXS'
                            : 'CircleTickSolidXS'
                        }
                        color={item.taxYear === TAX_YEAR_2019 ? 'warning' : 'active'}
                      />
                      <Text type="paragraphs" size="M_L_S" color="dark">
                        {item.taxYear}
                      </Text>
                    </FlexContainer>
                    {item.taxYear === TAX_YEAR_2019 ? (
                      <Styled.TextWarning type="paragraphs" size="S_L_S" color="warning_strong">
                        {item?.amountTotal?.toString().replace('-', '$')}
                      </Styled.TextWarning>
                    ) : (
                      <Text type="paragraphs" size="S_L_S" color="success_strong">
                        {item?.amountTotal
                          ?.toString()
                          .replace('-', '+')
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      </Text>
                    )}
                  </FlexContainer>
                  {item.taxYear === TAX_YEAR_2019 && (
                    <Banner
                      label=""
                      id="banner-2019"
                      description={t('PreviousYear.2019.data.banner')}
                      mode="warning"
                      size={Sizes.S}
                      hasIcon={false}
                      layout="embed"
                    />
                  )}
                </FlexContainer>
              </Layout.Wrap>
            ))}
            {data?.compensatedTaxReturns && (
              <Layout.Wrap size="S">
                <FlexContainer direction="column" gap="dimension_08">
                  <FlexContainer align="center" justify="space-between">
                    <FlexContainer align="center" gap="dimension_04">
                      <Icon icon="CircleTickSolidXS" color="cold" />
                      <Text type="paragraphs" size="M_L_S" color="dark">
                        {TAX_YEAR}
                      </Text>
                    </FlexContainer>
                    <Text type="paragraphs" size="S_L_S" color="cold">
                      {`-${data?.compensatedTaxReturns?.[0]?.amount}`}
                    </Text>
                  </FlexContainer>
                  <Banner
                    label=""
                    id="banner-2019"
                    description={t('PreviousYear.compensated.data.banner')}
                    mode="cold"
                    size={Sizes.S}
                    hasIcon={false}
                    layout="embed"
                  />
                </FlexContainer>
              </Layout.Wrap>
            )}
            {(sortArrPerYear.length > 1 || yearsMissingSatStatus?.length > 0 || taxYear2019) && (
              <Styled.TotalToRecover size="S">
                <FlexContainer direction="column" gap="dimension_08">
                  <FlexContainer align="center" justify="space-between">
                    <FlexContainer align="center" gap="dimension_04">
                      <Icon icon="WarningRoundedSolidXS" color="success" />
                      <Text type="paragraphs" size="M_L_S" color="dark">
                        {t('PreviousYear.year.data.banner', {
                          context: yearsMissingSatStatus?.length > 0 && 'withPending',
                        })}
                      </Text>
                    </FlexContainer>
                    <Text type="paragraphs" size="S_L_S" color="success_strong">
                      {`$${Math.abs(data?.amountPending ?? 0)
                        .toString()
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`}
                    </Text>
                  </FlexContainer>
                  {yearsMissingSatStatus?.length > 0 && (
                    <Text type="paragraphs" size="S_L_S" color="success_strong">
                      {t('PreviousYear.pending.years', {
                        context: yearsMissingSatStatus?.length === 1 && 'oneYear',
                        years: yearsMissingSatStatus.join(', '),
                      })}
                    </Text>
                  )}
                </FlexContainer>
              </Styled.TotalToRecover>
            )}
          </>
        )}
      </FlexContainer>
    </FlexContainer>
  );
};
export default YearData;
